import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TablePagination,
  Chip,
  Divider,
  CircularProgress,
  alpha,
  MenuItem,
  Grid,
  useTheme,
  InputAdornment,
  Card,
  CardContent,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar
} from '@mui/material';
import ReceiptView from './ReceiptView';
import {
  Add as AddIcon,
  Paid as PaidIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowDownward as ArrowDownwardIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';
import { printReceipt } from '../../../utils/printVoucher';
import { checkVouchersHaveTransactions } from '../../../services/financeService';

// تعريف نوع بيانات سند القبض
interface IAccount {
  id: string;
  name: string;
}

interface IReceipt {
  id: string;
  receiptNumber: string;
  date: string;
  amount: number;
  debitAccount: IAccount;
  creditAccount: IAccount;
  description: string;
  status: string;
  createdBy: string;
  createdAt: string;
  customer: string;
  branchId?: string;
  branchName?: string;
}

interface VoucherTransactionStatus {
  status: 'complete' | 'partial' | 'missing';
  voucherAmount: number;
  debitAmount: number;
  creditAmount: number;
  transactionsCount: number;
  expectedTransactions: number;
  debitAccount: string;
  creditAccount: string;
  hasDebitTransaction: boolean;
  hasCreditTransaction: boolean;
  issues: string[] | null;
}

// مكون مؤشر حالة المعاملات
interface TransactionStatusIndicatorProps {
  transactionStatus: VoucherTransactionStatus | null;
  loading: boolean;
  voucherNumber: string;
}

const TransactionStatusIndicator: React.FC<TransactionStatusIndicatorProps> = ({
  transactionStatus,
  loading,
  voucherNumber
}) => {
  if (loading) {
    return <CircularProgress size={20} />;
  }

  if (!transactionStatus) {
    return <Chip label="لا توجد بيانات" size="small" color="default" />;
  }

  const getStatusIcon = (): React.ReactElement | undefined => {
    switch (transactionStatus.status) {
      case 'complete':
        return <CheckCircleIcon />;
      case 'partial':
        return <WarningIcon />;
      case 'missing':
        return <ErrorIcon />;
      default:
        return undefined;
    }
  };

  const getStatusColor = (): 'success' | 'warning' | 'error' => {
    switch (transactionStatus.status) {
      case 'complete':
        return 'success';
      case 'partial':
        return 'warning';
      case 'missing':
        return 'error';
      default:
        return 'default' as any;
    }
  };

  const getTooltipContent = () => {
    const lines = [
      `مبلغ السند: ${transactionStatus.voucherAmount.toFixed(2)} ريال`,
      `عدد المعاملات: ${transactionStatus.transactionsCount} من ${transactionStatus.expectedTransactions}`,
      `الحساب المدين: ${transactionStatus.debitAccount}`,
      `مبلغ المدين: ${transactionStatus.debitAmount.toFixed(2)} ريال`,
      `الحساب الدائن: ${transactionStatus.creditAccount}`,
      `مبلغ الدائن: ${transactionStatus.creditAmount.toFixed(2)} ريال`,
    ];

    if (transactionStatus.issues && transactionStatus.issues.length > 0) {
      lines.push('', 'المشاكل:');
      transactionStatus.issues.forEach(issue => lines.push(`• ${issue}`));
    }

    return lines.join('\n');
  };

  return (
    <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{getTooltipContent()}</div>} arrow>
      <Chip
        icon={getStatusIcon()}
        label={
          transactionStatus.status === 'complete' ? 'مكتمل' :
          transactionStatus.status === 'partial' ? 'جزئي' : 'مفقود'
        }
        size="small"
        color={getStatusColor()}
      />
    </Tooltip>
  );
};

// المكون الرئيسي
const ReceiptsList: React.FC = () => {
  const [receipts, setReceipts] = useState<IReceipt[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // حالة نافذة عرض السند
  const [viewReceiptId, setViewReceiptId] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // إضافة متغيرات التصفيح
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalReceipts, setTotalReceipts] = useState(0);
  
  // حالة المعاملات المالية
  const [voucherTransactions, setVoucherTransactions] = useState<Record<string, VoucherTransactionStatus>>({});
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();

  // تعريف وظيفة جلب البيانات للإستخدام المتكرر
  const fetchReceipts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // بناء URL مع pagination parameters
      let apiUrl = `/finance/receipts?page=${page + 1}&limit=${rowsPerPage}`;
      
      // إضافة البحث
      if (searchTerm) {
        apiUrl += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      // إضافة فلتر الحالة
      if (selectedStatus) {
        apiUrl += `&status=${selectedStatus}`;
      }
      
      // إضافة فلتر التاريخ
      if (startDate && endDate) {
        apiUrl += `&startDate=${startDate}&endDate=${endDate}`;
      }
      
      console.log('Fetching receipts with URL:', apiUrl);
      
      // استخدام المسار الصحيح للواجهة البرمجية
      const response = await apiService.get<any>(apiUrl);
      
      // Safely handle different response formats
      let receiptsData: IReceipt[] = [];
      let total = 0;
      
      if (Array.isArray(response)) {
        // Direct array response
        receiptsData = response;
        total = response.length;
      } else if (response && typeof response === 'object') {
        // Cast to Record<string, any> to safely access any property
        const responseObj = response as Record<string, any>;
        
        // Object response with data property (common API pattern)
        if (responseObj.data && Array.isArray(responseObj.data)) {
          receiptsData = responseObj.data;
          total = responseObj.total || responseObj.data.length;
        } else if (responseObj.receipts && Array.isArray(responseObj.receipts)) {
          receiptsData = responseObj.receipts;
          total = responseObj.total || responseObj.receipts.length;
        } else {
          console.error('Unexpected API response format:', response);
          setError('تنسيق استجابة API غير متوقع. يرجى التحقق من سجلات وحدة التحكم.');
        }
      } else {
        console.error('Unexpected API response type:', response);
        setError('نوع استجابة API غير متوقع');
      }
      
      // Always set receipts to an array, even if empty
      setReceipts(receiptsData);
      setTotalReceipts(total);
      
      console.log(`Loaded ${receiptsData.length} receipts (total: ${total})`);
      // طباعة أول سند للتحقق من البيانات
      if (receiptsData.length > 0) {
        console.log('Sample receipt data:', receiptsData[0]);
        console.log('createdBy value:', receiptsData[0].createdBy);
      }
    } catch (err) {
      console.error('خطأ في جلب سندات القبض:', err);
      setError('حدث خطأ أثناء جلب بيانات سندات القبض. يرجى المحاولة مرة أخرى.');
      // Ensure receipts is always an array
      setReceipts([]);
      setTotalReceipts(0);
    } finally {
      setLoading(false);
    }
  };

  // جلب بيانات سندات القبض من الخادم عند تحميل المكون أو تغيير pagination/filters
  useEffect(() => {
    fetchReceipts();
  }, [page, rowsPerPage, searchTerm, selectedStatus, startDate, endDate]);
  
  // تحقق مما إذا كان هناك طلب لتحديث البيانات (عند العودة من إضافة/تعديل)
  useEffect(() => {
    // استمع لأحداث تغيير التخزين المحلي
    const handleStorageChange = () => {
      const needsRefresh = localStorage.getItem('receipts_refresh_needed');
      if (needsRefresh === 'true') {
        // إزالة العلامة بعد التحديث
        localStorage.removeItem('receipts_refresh_needed');
        // إعادة تحميل البيانات
        fetchReceipts();
        
        // التحقق من وجود رسالة محددة
        const message = localStorage.getItem('receipts_refresh_message');
        // عرض إشعار بنجاح التحديث
        showNotification(message || 'تم تحديث قائمة سندات القبض', 'success');
        // مسح الرسالة
        localStorage.removeItem('receipts_refresh_message');
      }
    };
    
    // تحقق فور تحميل المكون
    handleStorageChange();
    
    // إضافة مستمع الأحداث
    window.addEventListener('storage', handleStorageChange);
    
    // إزالة المستمع عند إزالة المكون
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // جلب بيانات المعاملات المالية للسندات
  useEffect(() => {
    const fetchVoucherTransactions = async () => {
      if (receipts.length === 0) return;

      // تأخير 600ms قبل جلب البيانات
      const timer = setTimeout(async () => {
        try {
          setTransactionsLoading(true);
          const voucherNumbers = receipts.map(r => r.receiptNumber);
          const response = await checkVouchersHaveTransactions(voucherNumbers, 'receipt');
          
          if (response.success) {
            setVoucherTransactions(response.results);
          }
        } catch (error) {
          console.error('Error fetching voucher transactions:', error);
        } finally {
          setTransactionsLoading(false);
        }
      }, 600);

      return () => clearTimeout(timer);
    };

    fetchVoucherTransactions();
  }, [receipts]);

  // في Server-side Pagination، البيانات تأتي من Backend مع التصفية والترقيم

  // إعادة تعيين الفلاتر
  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSelectedStatus('');
    setPage(0); // إعادة تعيين الصفحة
  };
  
  // إعادة تعيين الصفحة عند تغيير البحث أو الفلاتر
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedStatus, startDate, endDate]);

  // فتح قائمة الإجراءات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, receiptId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedReceiptId(receiptId);
  };

  // إغلاق قائمة الإجراءات
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReceiptId(null);
  };

  // إغلاق رسالة الإشعار
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // عرض رسالة إشعار للمستخدم
  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // تحديث حالة سند القبض
  const handleChangeStatus = async (newStatus: string) => {
    if (selectedReceiptId) {
      try {
        setLoading(true);
        // استخدام المسار الصحيح للواجهة البرمجية
        await apiService.put(`/finance/receipts/${selectedReceiptId}/status`, { status: newStatus });
        
        // تحديث البيانات المحلية بعد التحديث الناجح
        setReceipts(prevReceipts =>
          prevReceipts.map(receipt =>
            receipt.id === selectedReceiptId
              ? { ...receipt, status: newStatus }
              : receipt
          )
        );
        
        // إعلام المكونات الأخرى بالتغيير
        localStorage.setItem('receipts_refresh_needed', 'true');
        
        showNotification(`تم تغيير حالة السند إلى ${newStatus} بنجاح`, 'success');
      } catch (err) {
        console.error('خطأ في تحديث حالة السند:', err);
        showNotification('حدث خطأ أثناء تحديث حالة السند', 'error');
      } finally {
        setLoading(false);
        handleMenuClose();
      }
    }
  };

  // حذف سند القبض
  const handleDeleteReceipt = async () => {
    if (selectedReceiptId) {
      try {
        setLoading(true);
        // استخدام المسار الصحيح للواجهة البرمجية
        await apiService.delete(`/finance/receipts/${selectedReceiptId}`);
        
        // حذف السند من البيانات المحلية بعد الحذف الناجح
        setReceipts(prevReceipts =>
          prevReceipts.filter(receipt => receipt.id !== selectedReceiptId)
        );
        
        // إعلام المكونات الأخرى بالتغيير
        localStorage.setItem('receipts_refresh_needed', 'true');
        
        showNotification('تم حذف السند بنجاح', 'success');
      } catch (err) {
        console.error('خطأ في حذف السند:', err);
        showNotification('حدث خطأ أثناء حذف السند', 'error');
      } finally {
        setLoading(false);
        handleMenuClose();
      }
    }
  };

  // طباعة السند
  const handlePrintReceipt = async (receiptId: string) => {
    try {
      await printReceipt(receiptId);
    } catch (error) {
      console.error('خطأ في طباعة السند:', error);
      showNotification('حدث خطأ أثناء طباعة السند', 'error');
    }
  };

  // عرض شريحة الحالة
  const renderStatus = (status: string) => {
    let color;
    let icon;
    
    switch (status) {
      case 'مؤكد':
        color = 'success';
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case 'تحت الموافقة':
        color = 'warning';
        icon = <FilterListIcon fontSize="small" />;
        break;
      case 'مرفوض':
        color = 'error';
        icon = <CancelIcon fontSize="small" />;
        break;
      default:
        color = 'default';
        icon = null;
    }
    
    return (
      <Chip 
        size="small" 
        label={status}
        color={color}
        icon={icon}
        sx={{ 
          fontWeight: 500,
          fontSize: '0.75rem'
        }}
      />
    );
  };

  // وظائف التصفيح
  const handleChangePage = (event: unknown, newPage: number) => {
    console.log(`تغيير الصفحة من ${page} إلى ${newPage}`);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log(`تغيير عدد السندات في الصفحة من ${rowsPerPage} إلى ${newRowsPerPage}`);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // في Server-side Pagination، نستخدم receipts مباشرة من API
  // لأن الخادم يرسل فقط السندات الخاصة بالصفحة الحالية

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
            background: 'white'
          }}
        >
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <PaidIcon sx={{ mr: 1, color: '#30c3b0' }} />
              سندات القبض
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/finance/receipts/new')}
              sx={{
                bgcolor: '#30c3b0',
                '&:hover': { bgcolor: '#25a899' },
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
              }}
            >
              إضافة سند قبض
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* عرض رسالة الخطأ إذا كانت موجودة */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              icon={<ErrorIcon fontSize="inherit" />}
            >
              {error}
            </Alert>
          )}
          
          <Card variant="outlined" sx={{ mb: 3, borderColor: alpha(theme.palette.primary.main, 0.1) }}>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}>
                <FilterListIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                البحث والتصفية
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    placeholder="بحث برقم السند، الوصف أو العميل"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    select
                    label="حالة السند"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">جميع الحالات</MenuItem>
                    <MenuItem value="مؤكد">مؤكد</MenuItem>
                    <MenuItem value="تحت الموافقة">تحت الموافقة</MenuItem>
                    <MenuItem value="مرفوض">مرفوض</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    type="date"
                    label="من تاريخ"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    type="date"
                    label="إلى تاريخ"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetFilters}
                    sx={{ minWidth: 100 }}
                  >
                    إعادة تعيين
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
            </Box>
          ) : (
            <TableContainer component={Box}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>رقم السند</TableCell>
                    <TableCell>التاريخ</TableCell>
                    <TableCell>الفرع</TableCell>
                    <TableCell>العميل</TableCell>
                    <TableCell>الحساب الدائن</TableCell>
                    <TableCell align="left">المبلغ (ريال)</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>المعاملات</TableCell>
                    <TableCell>المستخدم</TableCell>
                    <TableCell align="center">الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map(receipt => (
                    <TableRow key={receipt.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{receipt.receiptNumber}</TableCell>
                      <TableCell>{new Date(receipt.date).toLocaleDateString('ar-SA')}</TableCell>
                      <TableCell>{receipt.branchName || 'غير محدد'}</TableCell>
                      <TableCell>{receipt.customer}</TableCell>
                      <TableCell>{receipt.creditAccount.name}</TableCell>
                      <TableCell align="left" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.success.main }} />
                          {receipt.amount.toLocaleString()}
                        </Box>
                      </TableCell>
                      <TableCell>{renderStatus(receipt.status)}</TableCell>
                      <TableCell>
                        <TransactionStatusIndicator
                          transactionStatus={voucherTransactions[receipt.receiptNumber] || null}
                          loading={transactionsLoading}
                          voucherNumber={receipt.receiptNumber}
                        />
                      </TableCell>
                      <TableCell>{receipt.createdBy}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="عرض" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setViewReceiptId(receipt.id);
                              setViewDialogOpen(true);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="تعديل" arrow>
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/finance/receipts/edit/${receipt.id}`}
                            sx={{ color: '#30c3b0' }}
                            disabled={receipt.status === 'مؤكد'}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="طباعة" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handlePrintReceipt(receipt.id)}
                            sx={{ color: theme.palette.grey[700] }}
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="المزيد من الخيارات" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, receipt.id)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {receipts.length === 0 && !loading && !error && (
            <Box sx={{
              py: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              opacity: 0.8
            }}>
              <PaidIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                لا توجد سندات قبض مطابقة
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                حاول تغيير معايير البحث أو إضافة سند قبض جديد
              </Typography>
            </Box>
          )}

          {/* إضافة شريط التصفيح */}
          {totalReceipts > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalReceipts}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="عدد السندات في الصفحة:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} من ${count}`
              }
              sx={{
                mt: 2,
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  '& .MuiTablePagination-spacer': { flex: '0 1 auto' }
                }
              }}
            />
          )}
          
          {/* قائمة الإجراءات */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 2,
              sx: { minWidth: 180, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }
            }}
          >
            <MenuItem onClick={() => handleChangeStatus('مؤكد')}>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
              </ListItemIcon>
              <ListItemText primary="تأكيد السند" />
            </MenuItem>
            <MenuItem onClick={() => handleChangeStatus('مرفوض')}>
              <ListItemIcon>
                <CancelIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText primary="رفض السند" />
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleDeleteReceipt} sx={{ color: theme.palette.error.main }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
              </ListItemIcon>
              <ListItemText primary="حذف السند" />
            </MenuItem>
          </Menu>
        </Paper>
      </Box>
      
      {/* رسائل الإشعار */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* نافذة عرض تفاصيل السند */}
      <ReceiptView
        receiptId={viewReceiptId}
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewReceiptId(null);
        }}
      />
    </>
  );
};

export default ReceiptsList;